import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import BackButton from "@/components/BackButton";
import { useAuth } from "@/contexts/AuthContext";
import { isNativeApp } from "@/lib/nativeBridge";
import NativePlanNotice from "@/components/NativePlanNotice";
import { CrownIcon as Crown, PawPrintIcon as PawPrint, HouseSimpleIcon as HouseSimple, SparkleIcon as Sparkle, ShieldCheckIcon as ShieldCheck, CertificateIcon as Certificate, VideoCameraIcon as VideoCamera, StorefrontIcon as Storefront, HeartIcon as Heart, ArrowsLeftRightIcon as ArrowsLeftRight, CheckCircleIcon as CheckCircle, StarIcon as Star, CaretRightIcon as CaretRight } from "@phosphor-icons/react";

/**
 * `/services` — full marketing-style catalog of every paid offering on
 * the platform, grouped by category. Single source-of-truth landing
 * for "what can I pay for here?" — replaces the cramped top-nav
 * dropdown so curious buyers and breeders can browse the whole menu
 * without losing their place.
 *
 * Public route — anonymous visitors can read every price and feature
 * list before they sign up; CTAs route through `/login` then back to
 * `/billing?plan=...` post-auth.
 *
 * Prices on this page mirror `PACKAGES` in
 * `backend/routers/billing/packages.py`. When PACKAGES changes, update
 * the labels below in one pass. Hard-coding (vs. fetching live) keeps
 * the page renderable for anonymous visitors without an auth round-trip.
 */

// Monthly / annual prices for all paid plans (mirror PACKAGES).
const SUBSCRIPTIONS = [
  {
    id: "buyer_pass",
    icon: PawPrint,
    title: "Buyer Pass",
    tag: "For buyers",
    monthly: 4.99,
    annual: 39,
    cta_monthly: "/billing?plan=buyer_pass_monthly",
    cta_annual: "/billing?plan=buyer_pass_annual",
    pitch: "Unlock the marketplace's hidden depth — full 6-generation pedigrees, breed alerts, watchlists, unlimited inquiries.",
    features: [
      "Unlimited breeder inquiries",
      "Save dogs and kennels to your watchlist",
      "Breed-match email alerts for new litters",
      "Full 6-generation pedigree visibility",
      "Priority message routing",
    ],
  },
  {
    id: "care_plus_dog_log",
    icon: PawPrint,
    title: "Dog Log",
    tag: "For owners who love their dogs",
    monthly: 9.99,
    annual: 69,
    cta_monthly: "/billing?plan=care_plus_monthly",
    cta_annual: "/billing?plan=care_plus_annual",
    pitch: "The diary your dog deserves — and your vet will love. Built for everyday dog owners who aren't breeders or buyers, just devoted humans.",
    features: [
      "10 one-tap log categories: meals, walks, potty, play, energy, weight, meds, symptoms, training, grooming",
      "Smart vet warnings when symptoms repeat (severity ≥3 for 2+ days)",
      "Streaks · weight trends · 7- and 30-day stats",
      "Appointments with iCal export (Google Calendar & Apple Calendar)",
      "3 downloadable PDFs: Vet Visit · Sitter Briefing · Sitter Return Report (with photo timeline)",
      "Sitter sharing — native share sheet, email, QR code, or copy",
      "Saturday weekly recap email",
      "Unlimited dogs · auto-included with Breeder Pro & Kennel Elite",
    ],
  },
  {
    id: "basic_plan",
    icon: HouseSimple,
    title: "Breeder Basic",
    tag: "For breeders",
    monthly: 19,
    annual: 190,
    cta_monthly: "/billing?plan=basic_plan_v2",
    cta_annual: "/billing?plan=basic_plan_v2_annual",
    pitch: "Light-touch tools for hobby breeders running 1–2 litters a year.",
    features: [
      "10 dog registrations / year",
      "4 litter registrations / year",
      "Free single-dog marketplace listings (5 active)",
      "Heat cycle tracking + breeding reminders",
      "Public kennel profile + auto share cards",
    ],
  },
  {
    id: "pro_plan",
    icon: Crown,
    title: "Breeder Pro",
    tag: "Most popular",
    highlight: true,
    monthly: 44,
    annual: 420,
    cta_monthly: "/billing?plan=pro_plan_v2",
    cta_annual: "/billing?plan=pro_plan_v2_annual",
    pitch: "Unlimited everything + Verified Pro wax-seal + 9% platform fee. The badge buyers look for.",
    features: [
      "Unlimited dog & litter registrations",
      "Unlimited marketplace + Upcoming Litter listings",
      "Free unlimited pedigree certificates",
      "Verified Pro wax-seal on every listing + share card",
      "Dog Log Care Hub + advanced analytics included",
      "9% platform fee · buyers earn 75% of every video tour",
    ],
  },
  {
    id: "kennel_elite",
    icon: Star,
    title: "Kennel Elite",
    tag: "Pays for itself",
    monthly: 129,
    annual: 1290,
    cta_monthly: "/billing?plan=kennel_elite_v2",
    cta_annual: "/billing?plan=kennel_elite_v2_annual",
    pitch: "Everything in Pro + 8% platform fee, cohort analytics, reputation defense, featured rotation.",
    features: [
      "Everything in Breeder Pro",
      "Elite Kennel wax-seal (crimson + gold) on listings + search results",
      "8% platform fee — save another 1% vs Pro on every sale",
      "Cohort analytics + lead-source attribution + waitlist nurture",
      "Reputation defense (right-of-reply + bad-faith protection)",
      "Featured rotation on Marketplace + Upcoming Litters home",
      "Video hero + pinned testimonials + award case",
      "2 staff sub-accounts · priority phone support",
    ],
  },
  {
    id: "kennel_storefront",
    icon: Storefront,
    title: "The Dog Shop",
    tag: "Sell direct to customers",
    monthly: 19,
    annual: 190,
    cta_monthly: "/billing?plan=storefront_monthly",
    cta_annual: "/billing?plan=storefront_annual",
    pitch: "Run your own /store/your-kennel page. Sell dog food (local delivery) and merch under your brand — direct Stripe payouts, you keep the customer relationship.",
    features: [
      "Your own /store/your-kennel URL + listing in the public directory",
      "Dog food and merch catalog (you upload photos, set prices)",
      "Local-delivery ZIP allowlist + email exceptions",
      "Per-store branding: hero image + accent color",
      "Direct Stripe Connect payouts (we never hold your money)",
      "5% platform fee per sale — no other markups, no hidden fees",
      "$29/mo optional Featured rotation for pinned-to-top placement",
    ],
  },
];

// One-time / à-la-carte offerings.
const VERIFICATION = [
  {
    id: "kennel_verification",
    icon: ShieldCheck,
    title: "Verified Kennel Badge",
    price: 49,
    cadence: "one-time",
    cta: "/billing?plan=kennel_verification",
    pitch: "Identity + business verification. Get the gold Verified badge across every listing you publish, your kennel page, and the kennel directory.",
    bullets: [
      "Government-ID + business doc review",
      "Gold Verified badge everywhere your kennel shows up",
      "Boosts search ranking in marketplace results",
    ],
  },
  {
    id: "elite_verified_annual",
    icon: Sparkle,
    title: "Elite Verified Breeder",
    price: 199,
    cadence: "per year",
    cta: "/billing?plan=elite_verified_annual",
    pitch: "Annual prestige tier. Gold Elite badge, priority placement, and a featured slot in the kennel directory hero.",
    bullets: [
      "Verified-kennel review (if not already verified)",
      "Gold Elite badge across all listings",
      "Priority marketplace placement",
      "Featured slot in the /kennels directory hero",
    ],
  },
  {
    id: "dna_pedigree_verification",
    icon: Sparkle,
    title: "DNA-Verified Pedigree",
    price: 75,
    cadence: "per dog",
    cta: "/dogs",
    pitch: "Submit your dog's DNA panel report. Our admin team reviews parentage and flips a gold DNA-Verified badge on this dog across every listing, your kennel page, and the pedigree share card.",
    bullets: [
      "Works with Embark / Wisdom Panel / VGL or equivalent",
      "Admin review within 2 business days",
      "Gold DNA-Verified badge across all public surfaces",
      "Re-uploads are free if the reviewer asks for a clearer scan",
    ],
  },
];

const REGISTRATION = [
  {
    id: "dog_registration",
    icon: Certificate,
    title: "Register a dog",
    price: 25,
    cadence: "per dog",
    cta: "/dogs/new",
    pitch: "Add a single dog to the pedigree database. COI calculated automatically.",
    bullets: [
      "Lifetime pedigree record",
      "Inbreeding warnings against the registry",
      "Free with any Breeder plan (up to plan limits)",
    ],
  },
  {
    id: "litter_registration",
    icon: Certificate,
    title: "Register a litter",
    price: 75,
    cadence: "per litter",
    cta: "/litters/new",
    pitch: "Register a full litter — sire, dam, and every puppy — in one flow.",
    bullets: [
      "All puppies linked to dam + sire automatically",
      "Per-puppy profiles created in bulk",
      "Optional: list the whole litter on the marketplace",
    ],
  },
  {
    id: "certificate_purchase",
    icon: Certificate,
    title: "Pedigree certificate",
    price: 14.99,
    cadence: "per dog",
    cta: "/dogs",
    pitch: "Printable 4-generation pedigree certificate, on heritage stationery, with your kennel mark.",
    bullets: [
      "Heritage paper PDF",
      "Co-founder signatures + registry seal",
      "Optional profile photo embedded",
      "Free unlimited with Breeder Pro",
    ],
  },
  {
    id: "transfer_ownership",
    icon: ArrowsLeftRight,
    title: "Transfer ownership",
    price: 15,
    cadence: "per transfer",
    cta: "/transfers/new",
    pitch: "Cleanly hand a registered dog to its new owner with platform-recorded chain of title.",
    bullets: [
      "Buyer or seller can initiate",
      "Admin review for fraud protection",
      "Lifetime chain-of-title on the dog's record",
    ],
  },
];

const LISTINGS = [
  {
    id: "dog_marketplace_listing",
    icon: Storefront,
    title: "List a dog on the marketplace",
    price: 9.99,
    cadence: "60 days",
    cta: "/marketplace",
    pitch: "Publish one dog to the marketplace where verified buyers browse.",
    bullets: [
      "Surfaced in buyer search + recommendations",
      "Free with any Breeder plan",
      "Buyer escrow built in — no parking-lot cash",
    ],
  },
  {
    id: "litter_marketplace_listing",
    icon: Storefront,
    title: "List a litter on the marketplace",
    price: 19.99,
    cadence: "60 days",
    cta: "/litters",
    pitch: "List every puppy in a litter at once. Set per-puppy asking prices later.",
    bullets: [
      "All puppies surfaced together for easier shopping",
      "Free with Breeder Pro or Kennel Elite",
    ],
  },
  {
    id: "marketplace_feature",
    icon: Star,
    title: "Featured marketplace placement",
    price: 29,
    cadence: "30 days",
    cta: "/marketplace",
    pitch: "Pin one of your active listings to the top of the marketplace for a month.",
    bullets: [
      "Top-of-results pin for 30 days",
      "Stackable with Verified / Elite ranking",
    ],
  },
  {
    id: "breeding_listing_30d",
    icon: Heart,
    title: "Breeding directory listing",
    price: 14.99,
    cadence: "30 days",
    priceSuffix: "from",
    cta: "/breeding",
    pitch: "List a dog in the breeding directory for breeders looking to mate.",
    bullets: [
      "30 / 90 / 365-day tiers ($14.99 / $29.99 / $49.99)",
      "Breeding matchmaker scoring against compatible studs & dams",
      "Reachable via in-platform messaging",
    ],
  },
];

const ENGAGEMENT = [
  {
    id: "video_tour",
    icon: VideoCamera,
    title: "Live video tour",
    price: 35,
    cadence: "15-min session",
    cta: "/video-tours",
    pitch: "Walk through the kennel live with the breeder, meet the puppy and its siblings, ask questions in real time. Charged only after the breeder accepts.",
    bullets: [
      "15-minute live HD video via Daily.co",
      "No charge unless the breeder confirms",
      "Full refund if the breeder cancels or no-shows",
    ],
  },
];

function fmtPrice(p) {
  return Number.isInteger(p) ? `$${p}` : `$${p.toFixed(2)}`;
}

function PlanCard({ plan, cadence, onGet }) {
  const Icon = plan.icon;
  const price = cadence === "annual" ? plan.annual : plan.monthly;
  const to = cadence === "annual" ? plan.cta_annual : plan.cta_monthly;
  return (
    <div
      data-testid={`plan-card-${plan.id}`}
      className={`relative rounded-2xl border bg-white p-7 flex flex-col ${
        plan.highlight
          ? "border-gold shadow-[0_24px_60px_rgba(201,162,39,0.18)]"
          : "border-[#E8E0CC] shadow-[0_8px_24px_rgba(0,0,0,0.04)]"
      }`}
    >
      {plan.highlight && (
        <Badge
          className="absolute -top-3 left-7 rounded-full bg-gold-gradient text-ink border-0 font-semibold text-[10px] tracking-[0.18em] uppercase px-3 py-1"
          data-testid={`plan-badge-${plan.id}`}
        >
          {plan.tag}
        </Badge>
      )}
      <div className="flex items-center gap-2.5 mb-1">
        <Icon size={22} weight="duotone" className="text-gold-dark" />
        <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#6B6B6B]">
          {plan.tag}
        </span>
      </div>
      <h3 className="font-serif text-2xl text-ink mb-2">{plan.title}</h3>
      <p className="text-sm text-[#4A4A4A] leading-relaxed mb-5">{plan.pitch}</p>
      <div className="mb-5">
        <span className="font-serif text-4xl text-ink font-medium">{fmtPrice(price)}</span>
        <span className="text-[13px] text-[#6B6B6B] ml-1.5">
          /{cadence === "annual" ? "yr" : "mo"}
        </span>
      </div>
      <ul className="space-y-2 mb-6 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-[13.5px] text-ink">
            <CheckCircle size={16} weight="fill" className="text-gold-dark mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Button
        className={`w-full rounded-full h-11 font-semibold ${
          plan.highlight
            ? "bg-gold-gradient text-ink hover:opacity-95 border-0 shadow-[0_8px_24px_rgba(201,162,39,0.35)]"
            : "bg-ink text-white hover:bg-crimson border-0"
        }`}
        onClick={() => onGet(to)}
        data-testid={`plan-cta-${plan.id}`}
      >
        Get {plan.title}
        <CaretRight size={14} weight="bold" className="ml-1" />
      </Button>
    </div>
  );
}

function OfferCard({ item, onGet }) {
  const Icon = item.icon;
  return (
    <div
      data-testid={`offer-card-${item.id}`}
      className="rounded-2xl border border-[#E8E0CC] bg-white p-6 flex flex-col hover:border-gold/60 hover:shadow-[0_18px_44px_rgba(201,162,39,0.10)] transition-all"
    >
      <div className="flex items-center justify-between mb-3">
        <Icon size={26} weight="duotone" className="text-gold-dark" />
        <div className="text-right">
          {item.priceSuffix && (
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6B6B6B]">
              {item.priceSuffix}
            </div>
          )}
          <div className="font-serif text-2xl text-ink font-medium">
            {fmtPrice(item.price)}
          </div>
          <div className="text-[11px] text-[#6B6B6B]">{item.cadence}</div>
        </div>
      </div>
      <h4 className="font-serif text-lg text-ink mb-1.5">{item.title}</h4>
      <p className="text-[13.5px] text-[#4A4A4A] leading-relaxed mb-4">{item.pitch}</p>
      <ul className="space-y-1.5 mb-5 flex-1">
        {item.bullets.map((b) => (
          <li key={b} className="flex items-start gap-2 text-[12.5px] text-[#3A3A3A]">
            <span className="mt-1 h-1 w-1 rounded-full bg-gold-dark shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <Button
        variant="outline"
        className="w-full rounded-full h-10 border-ink text-ink hover:bg-ink hover:text-white font-medium"
        onClick={() => onGet(item.cta)}
        data-testid={`offer-cta-${item.id}`}
      >
        Get this
        <CaretRight size={13} weight="bold" className="ml-1" />
      </Button>
    </div>
  );
}

export default function Services() {
  // App Store 3.1.1 — no plan/paid-service pricing in the native shell.
  if (isNativeApp()) return <NativePlanNotice />;
  return <ServicesWeb />;
}

function ServicesWeb() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cadence, setCadence] = useState("monthly");

  // Anonymous visitor → route through /login with a `next=` so they
  // return to the same offer after they auth. Authed users go straight
  // to the destination.
  function handleGet(to) {
    if (user) {
      navigate(to);
    } else {
      navigate(`/login?next=${encodeURIComponent(to)}`);
    }
  }

  return (
    <div className="min-h-screen bg-[#FAF7EE]" data-testid="services-page">
      <Helmet>
        <title>Services & pricing · Armstrong K9 Registry</title>
        <meta
          name="description"
          content="Every membership, registration, certification, and listing service the registry offers — with transparent pricing."
        />
      </Helmet>

      {/* Top nav — light, public-friendly */}
      <header className="bg-ink text-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 sm:px-7 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="services-nav-home">
            <img src="/logo.png" alt="" className="h-8 w-8" />
            <span className="font-serif text-[15px] tracking-wide">Armstrong K9 Registry</span>
          </Link>
          <nav className="hidden sm:flex items-center gap-6 text-[13px] text-white/85">
            <Link to="/" className="hover:text-gold transition-colors" data-testid="services-nav-home-link">Home</Link>
            <Link to="/marketplace" className="hover:text-gold transition-colors" data-testid="services-nav-marketplace">Marketplace</Link>
            <Link to="/video-tours" className="hover:text-gold transition-colors" data-testid="services-nav-video-tours">Video tours</Link>
            {!user && (
              <Link to="/login" className="hover:text-gold transition-colors" data-testid="services-nav-login">Sign in</Link>
            )}
            {user && (
              <Link to="/dashboard" className="hover:text-gold transition-colors" data-testid="services-nav-dashboard">Dashboard</Link>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-ink text-white overflow-hidden border-b border-gold/20">
        <div className="absolute inset-0 opacity-[0.04] [background-image:radial-gradient(circle_at_center,white_1px,transparent_1px)] [background-size:24px_24px]" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-7 pt-6">
          <BackButton fallback="/dashboard" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-7 pb-16 sm:pb-24 pt-6 sm:pt-10 text-center">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-light mb-4">
            Everything we offer · transparent pricing
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5">
            One place. Every service.
          </h1>
          <p className="text-white/75 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Memberships, registrations, certifications, listings, and live engagement —
            published with no hidden tiers. Read it, pick what fits, upgrade or downgrade any time.
          </p>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-px bg-gold-hairline" />
      </section>

      {/* SUBSCRIPTIONS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-7 py-14 sm:py-20" data-testid="services-memberships">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-dark mb-2">
              Recurring memberships
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl text-ink">Pick a tier that matches how you use the registry.</h2>
            <p className="text-[#6B6B6B] mt-2 max-w-2xl">
              Cancel any time from <Link to="/billing" className="underline decoration-gold underline-offset-4 hover:text-ink">Billing</Link>. Annual plans save 15–30 % versus monthly.
            </p>
          </div>
          <div className="inline-flex rounded-full bg-white border border-[#E8E0CC] p-1" data-testid="cadence-toggle">
            <button
              type="button"
              data-testid="cadence-monthly"
              className={`px-4 py-1.5 text-[12.5px] rounded-full transition-colors ${
                cadence === "monthly" ? "bg-ink text-white" : "text-ink hover:bg-[#FAF3DA]"
              }`}
              onClick={() => setCadence("monthly")}
            >
              Monthly
            </button>
            <button
              type="button"
              data-testid="cadence-annual"
              className={`px-4 py-1.5 text-[12.5px] rounded-full transition-colors ${
                cadence === "annual" ? "bg-ink text-white" : "text-ink hover:bg-[#FAF3DA]"
              }`}
              onClick={() => setCadence("annual")}
            >
              Annual · save more
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SUBSCRIPTIONS.map((p) => (
            <PlanCard key={p.id} plan={p} cadence={cadence} onGet={handleGet} />
          ))}
        </div>
      </section>

      {/* VERIFICATION & TRUST */}
      <section className="bg-white border-y border-[#EBE2C5]">
        <div className="max-w-7xl mx-auto px-5 sm:px-7 py-14 sm:py-20" data-testid="services-verification">
          <div className="mb-8">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-dark mb-2">
              Verification & trust
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl text-ink">
              Turn a profile into a credential.
            </h2>
            <p className="text-[#6B6B6B] mt-2 max-w-2xl">
              Identity-verified kennels sit higher in search, win more inquiries, and signal seriousness to discerning buyers.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {VERIFICATION.map((i) => (
              <OfferCard key={i.id} item={i} onGet={handleGet} />
            ))}
          </div>
        </div>
      </section>

      {/* REGISTRATION & PEDIGREE */}
      <section className="max-w-7xl mx-auto px-5 sm:px-7 py-14 sm:py-20" data-testid="services-registration">
        <div className="mb-8">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-dark mb-2">
            Registration & pedigree
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl text-ink">
            Build the official record. Print the certificate.
          </h2>
          <p className="text-[#6B6B6B] mt-2 max-w-2xl">
            Every dog gets a lifetime pedigree record with COI inbreeding warnings calculated against the entire registry. Pro members get free unlimited certificates.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {REGISTRATION.map((i) => (
            <OfferCard key={i.id} item={i} onGet={handleGet} />
          ))}
        </div>
      </section>

      {/* LISTINGS & DISTRIBUTION */}
      <section className="bg-white border-y border-[#EBE2C5]">
        <div className="max-w-7xl mx-auto px-5 sm:px-7 py-14 sm:py-20" data-testid="services-listings">
          <div className="mb-8">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-dark mb-2">
              Marketing & listings
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl text-ink">Reach buyers who actually buy.</h2>
            <p className="text-[#6B6B6B] mt-2 max-w-2xl">
              Verified buyers come to the marketplace because they've vetted us first. Free listings included with every Breeder plan; pay-as-you-go for casual sellers.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {LISTINGS.map((i) => (
              <OfferCard key={i.id} item={i} onGet={handleGet} />
            ))}
          </div>
        </div>
      </section>

      {/* LIVE ENGAGEMENT */}
      <section className="max-w-7xl mx-auto px-5 sm:px-7 py-14 sm:py-20" data-testid="services-engagement">
        <div className="mb-8">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-dark mb-2">
            Live engagement
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl text-ink">See the puppy before you wire the money.</h2>
          <p className="text-[#6B6B6B] mt-2 max-w-2xl">
            Optional live HD video tours — booked through the registry, paid through escrow, with full refunds if the breeder cancels.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-5 max-w-3xl">
          {ENGAGEMENT.map((i) => (
            <OfferCard key={i.id} item={i} onGet={handleGet} />
          ))}
        </div>
      </section>

      {/* International Lead Marketplace + US Kennel Verified Inquiry —
          two newer venue-only revenue surfaces. Kept editorial (not
          a card grid) because pricing is mode-dependent (Hybrid Lead
          Pass tiers) and varies by foreign jurisdiction; sending users
          to the dedicated landing pages is cleaner than trying to fit
          the whole pricing matrix into a 320px card. */}
      <section className="bg-white border-y border-[#EBE2C5]" data-testid="services-intl-marketplace">
        <div className="max-w-7xl mx-auto px-5 sm:px-7 py-14 sm:py-20">
          <div className="mb-8 max-w-3xl">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-dark mb-2">
              Lead-only services
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl text-ink">
              Verified leads to international service pros · captured marketplace inquiries for U.S. kennels.
            </h2>
            <p className="text-[#6B6B6B] mt-3">
              Two newer surfaces both built on a strict venue posture &mdash; Armstrong sells the
              introduction, never the service, never the money. Buyers pay a flat $9 verified intro
              fee that filters tire-kickers and funds dispute mediation.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="rounded-2xl border border-[#E8E0CC] bg-white p-7 flex flex-col" data-testid="services-intl-lead-pass">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#8A6A00] mb-2">For intl pros</div>
              <h3 className="font-serif text-2xl text-ink mb-2">International Lead Marketplace</h3>
              <p className="text-sm text-[#4A4A4A] mb-5 leading-relaxed">
                Five paused intl personas relaunched: couriers, walkers, sitters, groomers,
                trainers. Hybrid Lead Pass model removes the cold-start barrier &mdash; pros
                start free with a $199 refundable bond, then upgrade as their pipeline grows.
              </p>
              <ul className="space-y-2 mb-6 flex-1 text-[13.5px]">
                <li className="flex items-start gap-2 text-ink"><CheckCircle size={16} weight="fill" className="text-gold-dark mt-0.5 shrink-0" /><span><b>$9 one-time verification fee</b> &mdash; required to list, non-refundable, filters spam at the cheapest point in the funnel</span></li>
                <li className="flex items-start gap-2 text-ink"><CheckCircle size={16} weight="fill" className="text-gold-dark mt-0.5 shrink-0" /><span><b>Free tier after verification</b> &mdash; 1 lead/month at tier 1, grows to 2/month after 3 deliveries, 4/month after 10. <i>No bond, no held funds.</i></span></li>
                <li className="flex items-start gap-2 text-ink"><CheckCircle size={16} weight="fill" className="text-gold-dark mt-0.5 shrink-0" /><span><b>$29 / lead</b> &mdash; pay-per-lead, no commitment</span></li>
                <li className="flex items-start gap-2 text-ink"><CheckCircle size={16} weight="fill" className="text-gold-dark mt-0.5 shrink-0" /><span><b>$119 / $399 lead packs</b> &mdash; 5 or 20 prepaid credits, never expire</span></li>
                <li className="flex items-start gap-2 text-ink"><CheckCircle size={16} weight="fill" className="text-gold-dark mt-0.5 shrink-0" /><span><b>$199 / mo (or $1,990 / yr) Pro Sub</b> &mdash; unlimited leads + priority placement</span></li>
                <li className="flex items-start gap-2 text-ink"><CheckCircle size={16} weight="fill" className="text-gold-dark mt-0.5 shrink-0" /><span><b>$49 / 30 days featured</b> &mdash; top-of-results placement</span></li>
              </ul>
              <div className="flex flex-wrap gap-2">
                <Link to="/transport/international" className="inline-flex items-center gap-1.5 rounded-full bg-ink hover:bg-crimson text-white text-[13px] font-semibold px-5 h-10" data-testid="services-intl-transporter-link">
                  Intl transporters
                </Link>
                <Link to="/intl-pros" className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] hover:bg-cream text-ink text-[13px] font-semibold px-5 h-10" data-testid="services-intl-other-link">
                  Walkers · sitters · groomers · trainers
                </Link>
              </div>
            </div>

            <div className="rounded-2xl border border-[#E8E0CC] bg-cream p-7 flex flex-col" data-testid="services-kennel-verified-inquiry">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#8A6A00] mb-2">For U.S. buyers</div>
              <h3 className="font-serif text-2xl text-ink mb-2">Kennel Verified Inquiry</h3>
              <p className="text-sm text-[#4A4A4A] mb-5 leading-relaxed">
                Direct contact (phone / email / social) on every <code className="bg-white px-1 rounded">/breeders/&lt;slug&gt;</code> page
                is now hidden by default. Send a Verified Inquiry &mdash; we route it through the
                Armstrong email relay so the kennel gets your message and you get a paper trail.
              </p>
              <ul className="space-y-2 mb-6 flex-1 text-[13.5px]">
                <li className="flex items-start gap-2 text-ink"><CheckCircle size={16} weight="fill" className="text-gold-dark mt-0.5 shrink-0" /><span><b>$9 flat intro fee</b> &mdash; non-refundable, but tiny enough that serious buyers don't bounce</span></li>
                <li className="flex items-start gap-2 text-ink"><CheckCircle size={16} weight="fill" className="text-gold-dark mt-0.5 shrink-0" /><span><b>Elite Verified kennels bypass the gate</b> &mdash; their contact is shown publicly</span></li>
                <li className="flex items-start gap-2 text-ink"><CheckCircle size={16} weight="fill" className="text-gold-dark mt-0.5 shrink-0" /><span><b>30-day migration grandfather</b> for kennels created before 2026-08-15</span></li>
                <li className="flex items-start gap-2 text-ink"><CheckCircle size={16} weight="fill" className="text-gold-dark mt-0.5 shrink-0" /><span><b>Captured paper trail</b> &mdash; every inquiry is logged for both sides for dispute mediation</span></li>
              </ul>
              <div className="flex flex-wrap gap-2">
                <Link to="/breeders" className="inline-flex items-center gap-1.5 rounded-full bg-ink hover:bg-crimson text-white text-[13px] font-semibold px-5 h-10" data-testid="services-breeders-link">
                  Browse kennels
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-ink text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-7 py-16 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl mb-3">Not sure where to start?</h2>
          <p className="text-white/75 max-w-xl mx-auto mb-7">
            Browse the marketplace as a guest, or open a free account to start a watchlist.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild className="rounded-full bg-gold-gradient text-ink hover:opacity-95 border-0 h-12 px-7 font-semibold shadow-[0_8px_24px_rgba(201,162,39,0.35)]">
              <Link to="/marketplace" data-testid="services-cta-marketplace">Browse marketplace</Link>
            </Button>
            {!user && (
              <Button asChild variant="outline" className="rounded-full border-white/40 bg-transparent text-white hover:bg-white hover:text-ink h-12 px-7 font-semibold">
                <Link to="/register" data-testid="services-cta-register">Create a free account</Link>
              </Button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
